/**
 * `GET /api/venues/online-pivot` — the «Онлайн-обучение» block of the venues page (task T.F5).
 *
 * An ADDITIVE endpoint in its own api module (`apps/api/src/pivot/`): it shares the
 * `/api/venues` path prefix with the venues contract #6–#10 but is not one of them, so the
 * shapes live here rather than in `./venues` — the same "declared next to the module that
 * owns them" rule that file documents.
 *
 * The block counts the ONLINE-format applications (the stored `applications.format` column —
 * T.F1; T.Z98 retired the last raw-row derivation readers) across four funnel columns plus an
 * Итого, aggregated per venue, with a synthetic «Онлайн» summary row first.
 */

import { useQuery } from '@tanstack/react-query';

import { requestEnvelope, type Envelope } from './client';

/** One row: a venue that holds ONLINE applications, or the synthetic summary (`venueId: null`). */
export interface OnlinePivotRow {
  /** `null` marks the «Онлайн» summary row — the block's first, bold row. */
  venueId: number | null;
  name: string;
  /** Доп. информация на ЕПГУ ← `AWAITING_INFO`. */
  awaitingInfo: number;
  /** Ждёт ссылку ← `WAITING_LINK`. */
  awaitingLink: number;
  /** Одобрен ← `EVI_PASSED`. */
  approved: number;
  /** Ссылка отправлена ← `LINK_RECEIVED`. */
  linkSent: number;
  /**
   * ADDITIVE (T.Z44) — the same slice minus «Отказ»: the fill reading the venue card prints
   * as «занято (факт)» beside the online plan. Optional in the type so a payload from an
   * older api reads as "no fill reading" rather than as a broken shape.
   */
  fillCounted?: number;
  /** Итого — the row's whole ONLINE slice (REJECTED/NO_DATA included, so it can exceed the columns). */
  total: number;
}

export interface OnlinePivotDto {
  /** The summary row FIRST, then the venues in name order — the api's order, rendered as-is. */
  rows: OnlinePivotRow[];
  /**
   * ONLINE applications with no venue (organic/unmatched): `summary.total − Σ venue.total`.
   * Quoted by the block so the columns not adding up to the summary reads as expected.
   */
  unattributed: number;
}

export type OnlinePivotEnvelope = Envelope<OnlinePivotDto>;

export const ONLINE_PIVOT_PATH = '/venues/online-pivot';

/** Cache identity of the one block; it takes no parameter, so the key is constant. */
export const onlinePivotQueryKey = ['venues', 'online-pivot'] as const;

/**
 * One query for the whole block. Refetched on mount after 30s like the roster's #6: the block
 * sits above a table the manager is working through, so it must not go stale silently.
 */
export function useOnlinePivot() {
  return useQuery({
    queryKey: onlinePivotQueryKey,
    queryFn: async ({ signal }): Promise<OnlinePivotDto> =>
      (await requestEnvelope<OnlinePivotDto>('GET', ONLINE_PIVOT_PATH, ['data'], { signal })).data,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}
